import api from "./api";
import Logger from "../utils/Logger";

interface AttributeResponse {
  responseObject: AttributeListItem[];
}

export interface AttributeListItem {
  attributeId: number;
  attributeName: string;
  attributeDesc?: string;
  calendarYear: string;
  categoryDescription: string;
  categoryId: string;
  categoryName: string;
  edition: string;
  fromDate: string;
  isActive: boolean;
}

// Constants for categories
export const ATTRIBUTE_CATEGORIES = {
  OVERALL: "overall",
  ENVIRONMENTAL: "environmental",
  HUMANRIGHTS: "humanrights",
} as const;

export type AttributeCategory =
  (typeof ATTRIBUTE_CATEGORIES)[keyof typeof ATTRIBUTE_CATEGORIES];

/**
 * Fetches attributes from the API based on the provided category and active flag.
 * Defaults: category = "overall", active = true.
 * @param category - The category of attributes to fetch.
 * @param active - Whether to fetch only active attributes.
 * @returns A list of AttributeListItem.
 */
const attributesFromRemote = async (
  category: AttributeCategory = ATTRIBUTE_CATEGORIES.OVERALL,
  active: boolean = true,
): Promise<AttributeListItem[]> => {
  try {
    Logger.info(
      `Fetching attributes from API - category: ${category}, active: ${active}`,
      ["AttributeApi"],
    );
    const response = await api.get<AttributeResponse>(
      `/attribute?category=${category}&active=${active}`,
    );
    const attributeListItems = response?.data?.responseObject ?? [];

    if (!attributeListItems || !Array.isArray(attributeListItems)) {
      Logger.error(
        "API responseObject is invalid or not an array",
        attributeListItems,
        ["AttributeApi"],
      );
      return [];
    }

    Logger.info(
      `Successfully fetched ${attributeListItems.length} attributes`,
      ["AttributeApi"],
    );

    try {
      sessionStorage.setItem(
        `attributeListItemsStored_${category}_${active}`,
        JSON.stringify(attributeListItems),
      );
      Logger.debug("Cached attributes in session storage", ["AttributeApi"]);
    } catch (storageError) {
      Logger.error("Error saving data to sessionStorage", storageError, [
        "AttributeApi",
      ]);
    }

    return attributeListItems;
  } catch (err) {
    Logger.error("Error fetching attribute data", err, ["AttributeApi"]);
    return [];
  }
};

/**
 * Retrieves the attribute list from session storage or fetches it from the API.
 * @param category - The category of attributes to fetch.
 * @param active - Whether to fetch only active attributes.
 * @returns A list of AttributeListItem.
 */
const getAttributeList = async (
  category: AttributeCategory = ATTRIBUTE_CATEGORIES.OVERALL,
  active: boolean = true,
): Promise<AttributeListItem[]> => {
  const storageKey = `attributeListItemsStored_${category}_${active}`;
  const storedAttributeListItems = sessionStorage.getItem(storageKey);

  if (storedAttributeListItems) {
    Logger.debug(`Loading attributes from cache - ${storageKey}`, [
      "AttributeApi",
    ]);

    try {
      const parsedData = JSON.parse(storedAttributeListItems);

      if (!Array.isArray(parsedData)) {
        throw new Error("Parsed data is not an array");
      }

      Logger.info(`Loaded ${parsedData.length} attributes from cache`, [
        "AttributeApi",
      ]);
      return parsedData;
    } catch (err) {
      Logger.error("Error parsing stored attribute data", err, [
        "AttributeApi",
      ]);
      sessionStorage.removeItem(storageKey);
    }
  }

  Logger.debug("No cached attributes found, fetching from remote", [
    "AttributeApi",
  ]);
  return await attributesFromRemote(category, active);
};

export const AttributeApi = {
  getAttributeList,
};
